import moment from 'moment-timezone';
import { DestroyRef, Injectable } from '@angular/core';
import { of, shareReplay, timer, merge, Observable } from 'rxjs';
import { BrightnessSchedulePeriods } from '../models';
import { Intercom } from './intercom.service';
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { WatchdogService } from './watchdog.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WebsocketService } from './websocket.service';
import { KVRepository } from '../repositories';
import { ClusterService } from './cluster.service';
import { isEqual, isNotNull, isTruthy } from '../utils';
import { BrightnessConfig } from '../models';

const weekInSeconds = 604_800_000;
const defaultBrightness = 1;

@Injectable()
export class DeviceBrightness {

  private readonly storageKey = 'brightnessSettings';
  private readonly logger = this.watchdog.tag('Brightness Settings', 'blue');

  private readonly config$ = this.kvRepository.one$(this.storageKey).pipe(
    map((data) => {
      if (data?.value) {
        return data.value as BrightnessConfig;
      }

      return null;
    }),
    shareReplay(1),
  );

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly cluster: ClusterService,
    private readonly kvRepository: KVRepository,
    private readonly watchdog: WatchdogService,
    private readonly webSocket: WebsocketService,
    private readonly intercom: Intercom
  ) {}

  public initialize(): void {
    this.logger.info('Initialize');

    this.cluster.leader$.pipe(
      filter(isTruthy),
      switchMap(() => this.webSocket.messages$),
      filter((message) => message.type === 'brightnessSettings'),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((message) => {
      if (message.type === 'brightnessSettings') {
        this.update(message.data);
      }
    });


    // Send brightness to the device
    this.cluster.leader$.pipe(
      filter(isTruthy),
      // switchMap(() => this.intercom.connected$),
      // filter(isTruthy),
      switchMap(() => this.config$),
      switchMap((config) => {
        if (!config) {
          return of(null);
        }

        if (!config.schedule) {
          return of(config.control ? config.level : null);
        }

        const fallback = config.control ? config.level : null;
        const now = moment();

        const scheduled = config.schedulePeriods.filter((period) => {
          return period.active;
        }).map((period) => {
          const [startHour, startMinute] = period.startTime.split(':').map(Number);
          const start = now.clone().isoWeekday(period.startDay).hour(startHour).minute(startMinute);

          const starTimer = (startDue: Date) => timer(startDue, weekInSeconds).pipe(
            map(() => period.level)
          );

          const [endHour, endMinute] = period.endTime.split(':').map(Number);
          const end = now.clone().isoWeekday(period.startDay).hour(endHour).minute(endMinute);

          const endTimer = (startDue: Date) => timer(startDue, weekInSeconds).pipe(
            map(() => fallback)
          );

          if (now.isBetween(start, end)) {
            return [
              of(period.level),
              starTimer(start.add('1', 'weeks').toDate()),
              endTimer(end.toDate()),
            ];
          }

          if (end.isBefore(now)) {
            start.add('1', 'weeks');
            end.add('1', 'weeks');
          }

          return [
            starTimer(start.toDate()),
            endTimer(end.toDate())
          ];
        }).flat();

        if (scheduled.length === 0) {
          return of(fallback);
        }

        return merge(of(fallback),  ...scheduled);
      }),
      filter(isNotNull),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((level) => {
      this.logger.log('Set brightness level: ', level)
      this.intercom.call('brightness.set', level);
    });
  }

  public update(config: BrightnessConfig): void {
    this.config$.pipe(
      take(1),
      switchMap((configLocal) => {
        if (isEqual(configLocal, config)) {
          return of(config);
        }

        return this.kvRepository.update$({
          key: this.storageKey,
          value: config,
        });
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe();
  }
}
